/**
 * `/facilitator` — cross-workspace Facilitator dashboard (PLAN_DESIGN_ROOM_TIM.md F4 / RISET_DESIGN_ROOM_TIM.md §5.3).
 * Every Design Room the user owns or facilitates, aggregate stats vs the tier's room quota, and per-room participation
 * (a rubric a coach/lecturer can grade process with). Opens on any plan — a banner nudges non-facilitator plans to upgrade.
 */
import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { FacilitatorSummaryTiles } from '#/components/facilitator/summary-tiles'
import { FacilitatorRoomsTable } from '#/components/facilitator/rooms-table'
import { ParticipationDrawer } from '#/components/facilitator/participation-drawer'
import { useSubscription } from '#/lib/api'
import { useFacilitatorRooms } from '#/lib/api-facilitator'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/facilitator')({ component: FacilitatorPage })

const HAS_FACILITATOR_ACCESS = new Set(['facilitator', 'studio', 'admin'])

function FacilitatorPage() {
  const { data: session, isPending } = authClient.useSession()
  const navigate = useNavigate()
  useEffect(() => {
    if (!isPending && !session?.user) navigate({ to: '/sign-in', search: { redirect: '/facilitator' } })
  }, [isPending, session, navigate])
  const { data: sub } = useSubscription(Boolean(session?.user))
  const { data, isLoading } = useFacilitatorRooms(Boolean(session?.user))
  const [participationRoomId, setParticipationRoomId] = useState<string | null>(null)

  if (!session?.user) return null

  const planBase = sub?.planType?.replace(/-usd$/, '').replace(/-\d+mo$/, '') ?? 'free'
  const hasAccess = HAS_FACILITATOR_ACCESS.has(planBase)

  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        <div className="mx-auto w-full max-w-5xl space-y-5">
          <div>
            <h1 className="font-heading text-2xl font-semibold sm:text-3xl">{m.fac_dash_title()}</h1>
            <p className="text-sm text-muted-foreground">{m.fac_dash_subtitle()}</p>
          </div>
          {!hasAccess ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-center gap-2 text-sm"><Sparkles className="size-4 shrink-0 text-primary" /> {m.fac_dash_upgrade_banner()}</p>
              <Button size="sm" asChild className="shrink-0"><a href="/pricing#facilitator">{m.fac_dash_upgrade_cta()}</a></Button>
            </div>
          ) : null}
          {data?.summary ? <FacilitatorSummaryTiles summary={data.summary} /> : null}
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl border bg-muted/40" />)}
            </div>
          ) : (
            <FacilitatorRoomsTable rooms={data?.rooms ?? []} onParticipation={setParticipationRoomId} />
          )}
        </div>
      </main>
      <ParticipationDrawer roomId={participationRoomId} onClose={() => setParticipationRoomId(null)} />
    </>
  )
}
